"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { FileUploader } from "@/components/ui/FileUploader";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getPdfToolCopy } from "@/lib/pdf-tool-copy";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function ExcelToPdf() {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const copy = getPdfToolCopy(locale);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "excel-to-pdf") ?? tools[0],
    locale
  );

  const handleFilesSelected = (files: File[]) => {
    if (files.length > 0) {
      setFile(files[0]);
    }
  };

  const convertToPdf = async () => {
    if (!file) return;

    setIsProcessing(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/convert/excel-to-pdf', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Conversion failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${file.name.replace('.xlsx', '').replace('.xls', '')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error converting Excel to PDF:', error);
      alert(copy.errorConvertingExcelToPdf);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
           <div className="rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:table-large" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      {!file ? (
        <div className="space-y-4">
          <FileUploader 
            accept=".xls,.xlsx" 
            onFilesSelected={handleFilesSelected}
          />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="rounded-[30px] border-2 border-positivus-dark bg-white p-6 md:p-8">
            <div className="flex items-center gap-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-[20px] bg-green-100 text-green-600 border border-green-200">
                <Icon icon="mdi:file-excel" className="h-8 w-8" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-xl text-positivus-dark">{file.name}</p>
                <p className="text-gray-500 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
               <button 
                onClick={() => setFile(null)}
                className="text-gray-400 hover:text-red-500 p-2"
                title={ui.remove}
              >
                <Icon icon={uiIcons.refresh} className="h-6 w-6" />
              </button>
            </div>
             <div className="mt-6">
                <button
                  onClick={convertToPdf}
                  disabled={isProcessing}
                  className="w-full rounded-[20px] bg-positivus-dark px-6 py-4 text-xl font-medium text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:bg-positivus-dark disabled:hover:text-white"
                >
                  {isProcessing ? (
                      <div className="flex items-center justify-center gap-3">
                          <Icon icon="mdi:loading" className="h-6 w-6 animate-spin" />
                          <span>{copy.convertingToPdf}</span>
                      </div>
                  ) : copy.convertToPdf}
                </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
}
